import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useApi, useAuth } from '../auth.js';
import { ErrorBox, Spinner } from '../components.js';
import { AddEdgeForm } from '../EdgeLifecycle.js';
import { useResource } from '../hooks.js';
import { formatCount } from '../lib/format.js';
import { isPlatform } from '../lib/permissions.js';
import type { SitePageState } from './SitePage.js';

export function SitesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const sites = useResource('sites', () => api.sites());
  const [adding, setAdding] = useState(false);
  const platform = isPlatform(useAuth().user);
  const [tenantId, setTenantId] = useState('');
  const tenants = [
    ...new Map((sites.data ?? []).map((s) => [s.tenant.id, s.tenant.name])).entries(),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const shown = sites.data?.filter((s) => !tenantId || s.tenant.id === tenantId);

  return (
    <section>
      <div className="page-head">
        <h1>Sites</h1>
        <div className="page-actions">
          {platform && tenants.length > 1 && (
            <select
              aria-label="Tenant"
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
              }}
            >
              <option value="">All tenants</option>
              {tenants.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {platform && (
            <button
              type="button"
              className="btn"
              disabled={adding || !sites.data}
              onClick={() => {
                setAdding(true);
              }}
            >
              Add edge
            </button>
          )}
        </div>
      </div>
      {adding && sites.data && (
        <AddEdgeForm
          sites={sites.data}
          onCancel={() => {
            setAdding(false);
          }}
          onCreated={(created, token, tokenError) => {
            if (tokenError) {
              window.alert(`Edge created, but no install command: ${tokenError.message}`);
            }
            const state: SitePageState = token
              ? {
                  panel: {
                    kind: 'install',
                    edgeNodeId: created.edge_node_id,
                    token,
                    created: true,
                  },
                }
              : {};
            void navigate(`/sites/${encodeURIComponent(created.site_id)}`, { state });
          }}
        />
      )}
      {sites.error && <ErrorBox error={sites.error} onRetry={sites.reload} />}
      {!sites.data && sites.loading && <Spinner />}
      {sites.data?.length === 0 && !adding && (
        <p className="empty">
          {platform ? 'No sites yet. Add the first edge to create one.' : 'No sites yet.'}
        </p>
      )}
      {shown && shown.length > 0 && (
        <div className="cards">
          {shown.map((s) => (
            <Link key={s.id} to={`/sites/${s.id}`} className="card card-link">
              <div className="card-title">{s.name}</div>
              <div className="muted small">
                {s.tenant.name} · group <code>{s.sparkplug_group_id}</code>
              </div>
              <dl className="card-stats">
                <div>
                  <dt>Edges</dt>
                  <dd>{formatCount(s.edges)}</dd>
                </div>
                <div>
                  <dt>Points</dt>
                  <dd>{formatCount(s.points)}</dd>
                </div>
                <div>
                  <dt>Time zone</dt>
                  <dd className="small">{s.timezone}</dd>
                </div>
              </dl>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
